/**
 * Login Page
 * Supports both password and PIN authentication
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { UserIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { ApiException } from '@/services/api';
import { OPERATOR_NAME } from '@/config/branding';

type LoginMode = 'password' | 'pin';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [mode, setMode] = useState<LoginMode>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'password') {
        await login({ username, password });
      } else {
        await login({ username, pin });
      }

      // Redirect to the page they tried to visit or dashboard
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    if (!username.trim()) return false;
    if (mode === 'password') return password.trim().length > 0;
    if (mode === 'pin') return pin.length === 5 && /^\d+$/.test(pin);
    return false;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {OPERATOR_NAME}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Charter crew management
          </p>
        </div>

        {/* Login Card */}
        <Card variant="elevated">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
          </CardHeader>
          <CardBody>
            {/* Mode Toggle */}
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={mode === 'password' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('password')}
                className="flex-1"
              >
                Password
              </Button>
              <Button
                type="button"
                variant={mode === 'pin' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('pin')}
                className="flex-1"
              >
                PIN
              </Button>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <Input
                label="Username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                leftIcon={<UserIcon className="w-5 h-5" />}
                required
                autoComplete="username"
                disabled={isLoading}
              />

              {/* Password or PIN */}
              {mode === 'password' ? (
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftIcon={<LockClosedIcon className="w-5 h-5" />}
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              ) : (
                <Input
                  label="PIN"
                  type="text"
                  placeholder="Enter 5-digit PIN"
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setPin(value);
                  }}
                  leftIcon={<LockClosedIcon className="w-5 h-5" />}
                  maxLength={5}
                  required
                  autoComplete="off"
                  helpText="Pilots can use their 5-digit PIN for quick login"
                  disabled={isLoading}
                />
              )}

              {/* Error Message */}
              {error && (
                <div
                  className="p-3 rounded-md text-sm"
                  style={{
                    backgroundColor: 'var(--color-danger-bg)',
                    color: 'var(--color-danger)',
                    border: '1px solid var(--color-danger)',
                  }}
                  role="alert"
                >
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={isLoading}
                disabled={!isFormValid() || isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

          </CardBody>
        </Card>
      </div>
    </div>
  );
}
